import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import paymentStyles from '../../../common/styles/payment-styles';
import { BaseComponent } from '../../base-component/base-component';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { createLocalPaymentInstance, getLocalPaymentInstance, getLocalPaymentInstanceStatus } from '../braintree';
import { ActionStatus } from '../../../common/constants/action-status';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import emit from '../../../common/events';
import { getUserDataForUserAnalytics, selectUser } from '../../user-profile/user-profile.selectors';
import { PaymentDetails, PaymentType } from '../../checkout/shop-checkout.interface';
import { updatePaymentDetails } from '../../checkout/shop-checkout';
import { getCartAnalyticsOverrides } from '../../cart/shop-cart/shop-cart.analytics';
import { createDataCollector, getDataCollector } from '../braintree';
import { dispatchGenericErrorAlert, renderIdealButtonLoadingTemplate, renderIdealButtonTemplate } from './utils/ideal.util';
import { getCart } from '../../cart/shop-cart/store/shop-cart.selectors';
import { BraintreeLocalPaymentCancelledErrorCodes } from './interfaces/ideal.interfaces';

@customElement('mhecomm-ideal-button')
export default class IdealButton extends connect(store)(BaseComponent) {
  static styles = [paymentStyles];

  /**
   * Braintree Client token to initialize iDEAL button.
   */
  @property()
  clientToken = '';

  @property()
  merchantAccountId = '';

  /**
   * Transaction amount to be displayed in iDEAL.
   */
  @property({ type: Number })
  amount = 0;

  /**
   * Currency for iDEAL transaction.
   */
  @property()
  currency = 'EUR';

  @state()
  private isLoading = false;

  @state()
  private buttonInitialized = false;

  stateChanged(state: StoreState) {
    const localPaymentInstanceStatus = getLocalPaymentInstanceStatus(state);
    this.isLoading = [ActionStatus.idle, ActionStatus.loading].includes(localPaymentInstanceStatus);

    if (localPaymentInstanceStatus === ActionStatus.succeeded && !this.buttonInitialized) {
      this.buttonInitialized = true;
      this.createIdealButton();
    }
  }

  /**
   * @param _changedProperties
   */
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    await this.setupIdealButton();
  }

  async setupIdealButton() {
    const result = await store.dispatch(createLocalPaymentInstance({
      clientToken: this.clientToken,
      merchantAccountId: this.merchantAccountId,
    }));

    if (createLocalPaymentInstance.rejected.match(result) && !result.meta.condition) {
      console.error('Failed to create local payment instance:', (result.payload as any)?.message ?? result.error?.message);
      dispatchGenericErrorAlert();
    }
  }

  renderComp() {
    return html`
      ${this.isLoading
        ? renderIdealButtonLoadingTemplate()
        : renderIdealButtonTemplate()
      }
    `;
  }

  async createIdealButton() {
    const localPaymentInstance = getLocalPaymentInstance(store.getState() as StoreState);

    await waitForElementsToLoad(this.shadowRoot!, [
      '#ideal-button',
    ]);

    if (!localPaymentInstance) {
      return;
    }

    this.displayIdealButton(localPaymentInstance);
  }

  private idealButtonSelector() {
    return this.shadowRoot!.querySelector('#ideal-button') as HTMLElement;
  }

  private displayIdealButton(localPaymentInstance) {
    this.idealButtonSelector().addEventListener('click', () => {
      this.idealButtonSelector().classList.add('disabled');

      const state = store.getState() as StoreState;

      emit({
        element: this,
        dataLayer: true,
        name: `mhecomm-ideal-checkout-start`,
        detail: {
          label: 'iDEAL Checkout Start',
          url: window.location.href,
          user: getUserDataForUserAnalytics(state),
          orderTotal: this.amount,
          ...getCartAnalyticsOverrides(state),
        }
      });
      const cart = getCart(state);
      const user = selectUser(state);

      localPaymentInstance.startPayment({
        paymentType: 'ideal',
        paymentTypeCountryCode: cart?.products?.[0]?.priceInfo?.countryCode,
        fallback: {
          buttonText: 'Return to Merchant',
          url: window.location.href,
        },
        amount: this.amount.toFixed(2),
        currencyCode: cart?.products?.[0]?.priceInfo?.currencyCode,
        givenName: user?.givenName,
        surname: user?.familyName,
        address: {
          countryCode: cart?.products?.[0]?.priceInfo?.countryCode,
        },
        onPaymentStart: function (_data, start) {
          start();
        }
      }, (err, payload) => {
        if (err) {
          this.handleIdealError(err);
          this.idealButtonSelector().classList.remove('disabled');
          return;
        }
        this.handleIdealSuccess(payload);
        this.idealButtonSelector().classList.remove('disabled');
      });
    });
  }

  private handleIdealError(err) {
    switch (err.code) {
      case BraintreeLocalPaymentCancelledErrorCodes.LOCAL_PAYMENT_NOT_ENABLED:
      case BraintreeLocalPaymentCancelledErrorCodes.LOCAL_PAYMENT_CANCELED:
      case BraintreeLocalPaymentCancelledErrorCodes.LOCAL_PAYMENT_WINDOW_CLOSED:
        console.log('Customer canceled payment flow:', err.code);
        break;
      default:
        dispatchGenericErrorAlert();
    }
  }

  private async handleIdealSuccess(payload) {
    await store.dispatch(createDataCollector({
      clientToken: this.clientToken,
    }));

    const dataCollectorInstance = getDataCollector(store.getState() as StoreState);

    const paymentDetails: PaymentDetails = {
      paymentType: PaymentType.BRAINTREE_IDEAL,
      paymentNonce: payload.nonce,
      deviceData: dataCollectorInstance?.deviceData
    };

    emit({
      element: this,
      name: `mhecomm-ideal-checkout-info-collected`,
      dataLayer: true,
      detail: {
        paymentDetails: paymentDetails,
      },
      composed: true
    });

    store.dispatch(updatePaymentDetails(paymentDetails));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-ideal-button': IdealButton;
  }
}